import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import * as ReactIntl from 'react-intl';
import get from 'lodash/get';

/*
 * Used for testing only. Set global.defaultTranslations to false if you dont
 * wish for messages to have a default identifier during tests.
 */
const getIdOrDefault = (id, messages) => {
  const isTestEnvironment = process.env.NODE_ENV === 'test';
  const defaultsEnabled = global.defaultTranslationsEnabled;
  if (isTestEnvironment && defaultsEnabled && !messages[id]) {
    return 'default';
  }

  return id;
};

/*
 * In rails, each plural form has its own key, and we need to determine it
 * from the numeric value of <count>. A potential issue may arise since the algorithm
 * used here is not the same as in Rails.
 *
 * See:
 * http://guides.rubyonrails.org/i18n.html#pluralization.
 * https://github.com/yahoo/react-intl/wiki/API#formatplural
 * http://www.unicode.org/cldr/charts/latest/supplemental/language_plural_rules.html
 */
const getMessageId = (id, props, intl) => {
  let messageId = id;
  if (props.values && props.values.count !== undefined) {
    const pluralForm = intl.formatPlural(props.values.count, props);
    messageId = `${id}.${pluralForm}`;
  }

  return getIdOrDefault(messageId, intl.messages);
};

// Gets a translation given a key from global.translations.messages
// Returns undefined if key does not exist, otherwise returns the value
export const getTranslationForKey = key =>
  get(window.translations.messages, key);

export const {
  FormattedDate,
  FormattedTime,
  FormattedRelative,
  FormattedNumber,
  FormattedPlural,
  FormattedMessage,
  FormattedHTMLMessage,
} = ReactIntl;

export const TRANSLATED_CLASS = 'intl-text';
export const MISSING_CLASS = 'translation-missing';
export const DATA_I18N_KEY = 'data-i18n-key';

// Use this within other react components when you need a rendered translated message
export class TranslatedMessage extends React.Component {
  static contextTypes = {
    intl: ReactIntl.intlShape,
  };

  static propTypes = {
    id: PropTypes.string,
    values: PropTypes.object,
  };

  get className() {
    return (message, messageId) => {
      const baseClassName = classNames(TRANSLATED_CLASS, this.props.className);
      return classNames(baseClassName, {
        [MISSING_CLASS]: message === messageId,
      });
    };
  }

  get messageId() {
    const { id, ...props } = this.props;
    return getMessageId(id, props, this.context.intl);
  }

  renderMessageContent() {
    const parts = [].slice.call(arguments);
    const className = this.className(parts[0], this.messageId);
    const { textComponent: Text } = this.context.intl;
    const key = `js.${this.messageId}`;
    return React.createElement(
      Text,
      { className, [DATA_I18N_KEY]: key },
      ...parts
    );
  }

  render() {
    return (
      <ReactIntl.FormattedMessage {...this.props} id={this.messageId}>
        {this.renderMessageContent.bind(this)}
      </ReactIntl.FormattedMessage>
    );
  }
}

const missingIntlFunction = () => {
  console.error(
    `Translation error: No react-intl formatting function was provided to translateMessages`
  );
};

export const messageGenerator = messages => {
  return Object.values(messages).map(message => {
    if (typeof message === 'string') {
      return { id: message };
    }
    return message;
  });
};

/*
  message format:
    {
      name: identifying label for the translation, developer defined,
      id: react-intl, id corresponding to the flattened yml key,
      defaultMessage: react-intl, what will be displayed if no translation is found
        (if not provided, falls back to id)
      description: react-intl, context for the translator about how it's used
      values: react-intl, format for values to be inserted into the translation
        https://github.com/yahoo/react-intl/wiki/Components#formattedmessage
      value: react-intl, value for non-message translation, such as Date or Number
      options: react-intl, options that get passed to the various formatting
        functions and components for non-message components
    }
  output:
    {
      [name(defaults to id if name isn't provided)] : {
        raw: translated message/date/number as a raw string
        component: IntlComponent with the props from the message applied to it
        className: TRANSLATED_CLASS and, if missing a translation, MISSING_CLASS
      }
    }
*/

export const translateMessages = (messages = [], formatter) => {
  const translations = {};
  messages.forEach(({ name, ...message }) => {
    if (!name && !message.id) {
      console.error(
        `No name or id field was provided for translation: ${JSON.stringify(
          message
        )}`
      );
    }
    const nameBucket = name ? name : message.id;
    translations[nameBucket] = formatter(message);
  });
  return translations;
};

export const formatMessage = (
  intlFunc = missingIntlFunction,
  IntlComponent,
  intl
) => ({ values = {}, id, ...messageDescriptor }) => {
  const finalId = getMessageId(id, { values }, intl);
  const raw = intlFunc({ id: finalId, ...messageDescriptor }, values);
  const isMissingTranslation = raw === finalId;
  if (isMissingTranslation && typeof bugsnagClient !== 'undefined') {
    bugsnagClient.notify(`formatMessage: Missing translation id: ${id}`);
  }
  const key = `js.${id}`;
  let props = {
    id: finalId,
    ...messageDescriptor,
  };
  if (Object.keys(values).length > 0) {
    props = { ...props, values };
  }
  return {
    raw,
    component: IntlComponent ? React.createElement(IntlComponent, props) : null,
    className: classNames(TRANSLATED_CLASS, {
      [MISSING_CLASS]: isMissingTranslation,
    }),
    [DATA_I18N_KEY]: key,
  };
};

export const formatNonMessage = (
  intlFunc = missingIntlFunction,
  IntlComponent
) => ({ value, ...options }) => ({
  raw: intlFunc(value, options),
  component: IntlComponent
    ? React.createElement(IntlComponent, { value, ...options })
    : null,
  className: TRANSLATED_CLASS,
});

const MessageRenderProp = ({ intl, messages, children }) => {
  return children(
    translateMessages(
      messages,
      formatMessage(intl.formatMessage, TranslatedMessage, intl)
    ),
    intl
  );
};
/*
  Testing Caveat: to test, either do a full mount with an IntlProvider
    or use shallowWithIntl or mountWithIntl. If using shallow, to capture
    the output, you will have to call .dive() twice to get past the
    injectIntl HOC and the MessageRenderProp component. This applies to
    all of these IntlRenderProps
*/
export const IntlMessageRenderProp = ReactIntl.injectIntl(MessageRenderProp);

const DateRenderProp = ({ intl, dates, children }) =>
  children(
    translateMessages(dates, formatNonMessage(intl.formatDate, FormattedDate)),
    intl
  );

export const IntlDateRenderProp = ReactIntl.injectIntl(DateRenderProp);

const NumberRenderProp = ({ intl, numbers, children }) =>
  children(
    translateMessages(
      numbers,
      formatNonMessage(intl.formatNumber, FormattedNumber)
    ),
    intl
  );

export const IntlNumberRenderProp = ReactIntl.injectIntl(NumberRenderProp);

// Use this when you need to get just the translated string. Must provide intl.
export const translateMessage = (intl, id, values) => {
  const messageId = getMessageId(id, { values }, intl);
  return intl.formatMessage({ id: messageId }, values);
};

// Curried version of translateMessage.
export const messageTranslator = intl => (id, values) =>
  translateMessage(intl, id, values);

export class FormattedPercent extends React.Component {
  static propTypes = {
    value: PropTypes.string,
  };

  render() {
    if (!this.props.value) return null;

    const value = Number(this.props.value.replace('%', ''));
    if (value === 0) return null;

    return (
      <FormattedNumber
        value={value / 100}
        style="percent"
        minimumFractionDigits={0}
        maximumFractionDigits={1}
      />
    );
  }
}

export const IntlProvider = ({ children }) => {
  const onError = error => {
    if (typeof bugsnagClient !== 'undefined') {
      bugsnagClient.notify(error);
    }
  };

  return (
    <ReactIntl.IntlProvider
      locale={global.translations.locale}
      messages={global.translations.messages}
      onError={onError}
    >
      {children}
    </ReactIntl.IntlProvider>
  );
};

export const TranslatedLinkMessage = (
  { id, link, linkText, inlineData, opts = {} },
  ctx
) => {
  const templateKey = opts.templateKey || 'anchor';
  const linkData = translateMessage(ctx.intl, link);
  const linkTextData = translateMessage(ctx.intl, linkText);
  const anchor = <a href={linkData}>{linkTextData}</a>;
  return (
    <TranslatedMessage
      id={id}
      values={{ [templateKey]: anchor, ...inlineData }}
    />
  );
};

TranslatedLinkMessage.contextTypes = {
  intl: ReactIntl.intlShape,
};
